import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';

const NAV_EASE = [0.22, 1, 0.36, 1];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const navLinks = [
    { label: 'About', to: '/about', className: 'nav-link--about' },
    { label: 'Blog', to: '/blog', className: 'nav-link--blog' },
    { label: 'Contact', to: '/contact', className: 'nav-link--contact' },
  ];

  return (
    <>
      {/* Desktop header */}
      <div className="landing-header-shell landing-header-shell--desktop hidden lg:flex">
        <motion.header
          initial={{ y: -56, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: NAV_EASE, delay: 0.1 }}
          className="landing-header-shell__inner"
        >
          <div className="landing-nav-glass landing-nav-glass--desktop">
            <div className="landing-nav-glass__cluster landing-nav-glass__cluster--start">
              <Link to="/" aria-label="Stavyn" className="landing-nav-glass__brand">
                <Logo variant="auto" className="landing-nav-glass__logo" />
              </Link>

              <ThemeToggle />

              <div className="landing-nav-glass__divider" aria-hidden />
            </div>

            <nav className="landing-nav-glass__center" aria-label="Main">
              {navLinks.map((link) => (
                <Link key={link.label} to={link.to} className={`nav-link ${link.className}`}>
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="landing-nav-glass__cluster landing-nav-glass__cluster--end">
              <div className="landing-nav-glass__divider" aria-hidden />

              <div className="landing-nav-glass__actions">
                <Link
                  to={isAuthenticated ? '/app/dashboard' : '/login'}
                  className="nav-link landing-nav-glass__login"
                >
                  {isAuthenticated ? 'Dashboard' : 'Log In'}
                </Link>

                <Link to="/schedule-demo" className="landing-nav-glass__cta">
                  Free Risk Report
                  <svg viewBox="0 0 10 10" fill="none" aria-hidden>
                    <path
                      d="M1.5 5h7M6 2l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </motion.header>
      </div>

      {/* Mobile header */}
      <motion.header
        initial={{ y: -56, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: NAV_EASE, delay: 0.1 }}
        className="landing-header-shell landing-header-shell--mobile lg:hidden"
      >
        <div className="landing-nav-glass landing-nav-glass--mobile">
          <Link to="/" aria-label="Stavyn" className="landing-nav-glass__brand">
            <Logo variant="auto" className="landing-nav-glass__logo" />
          </Link>

          <div className="landing-nav-glass__actions" style={{ gap: '0.75rem' }}>
            <ThemeToggle />

            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              className="landing-nav-glass__menu-btn"
              style={{
                background: 'none',
                border: 'none',
                padding: '4px 6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <div style={{ width: 18, height: 12, position: 'relative' }}>
                <motion.span
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: 1.5,
                    background: 'currentColor',
                    display: 'block',
                    borderRadius: 1,
                  }}
                  animate={{ y: mobileOpen ? 6 : 0, rotate: mobileOpen ? 45 : 0 }}
                  transition={{ duration: 0.25, ease: NAV_EASE }}
                />
                <motion.span
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    width: '100%',
                    height: 1.5,
                    background: 'currentColor',
                    display: 'block',
                    marginTop: -0.75,
                    borderRadius: 1,
                  }}
                  animate={{ opacity: mobileOpen ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                />
                <motion.span
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: 1.5,
                    background: 'currentColor',
                    display: 'block',
                    borderRadius: 1,
                  }}
                  animate={{ y: mobileOpen ? -6 : 0, rotate: mobileOpen ? -45 : 0 }}
                  transition={{ duration: 0.25, ease: NAV_EASE }}
                />
              </div>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.25, ease: NAV_EASE }}
              className="landing-nav-glass landing-nav-glass--dropdown"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, ease: NAV_EASE, delay: i * 0.05 }}
                  >
                    <Link
                      to={link.to}
                      className="nav-link"
                      style={{ padding: '0.5625rem 0', display: 'block' }}
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div
                style={{ height: 1, background: 'var(--border-divider)', margin: '0.875rem 0' }}
                aria-hidden
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Link
                  to={isAuthenticated ? '/app/dashboard' : '/login'}
                  onClick={() => setMobileOpen(false)}
                  className="nav-link"
                  style={{
                    textAlign: 'center',
                    padding: '0.5rem',
                    display: 'block',
                  }}
                >
                  {isAuthenticated ? 'Dashboard' : 'Log In'}
                </Link>
                <Link
                  to="/schedule-demo"
                  onClick={() => setMobileOpen(false)}
                  className="landing-nav-glass__cta landing-nav-glass__cta--dropdown"
                >
                  Free Risk Report
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
}
